import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { Box } from '@mui/material';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';

import {
    getRouteForbidden,
    getRouteReportsSavingsSummary,
    getRouteReportsSavingsSummaryProduct,
} from 'shared/const/router';
import { formatReportCurrency } from 'shared/lib/reportFormatters';
import { ReportDrillInHeader } from 'shared/ui/ReportDrillInHeader';
import { ReportMetric } from 'shared/ui/ReportMetric/ReportMetric';
import { Page } from 'widgets/Page';
import { SavingsSummaryCategoriesSection } from 'widgets/SavingsSummaryReport/ui/SavingsSummaryCategoriesSection';
import { SavingsSummaryProductsSection } from 'widgets/SavingsSummaryReport/ui/SavingsSummaryProductsSection';

import { useReportsPageSavingsSummaryProperty } from '../model/hooks/useReportsPageSavingsSummaryProperty';

import type { ProductMetricsDto } from 'entities/ReportSavingsSummary';

export default () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const {
        isSavingsSummaryVisible,
        propertyId,
        propertyName,
        loading,
        summary,
        byCategory,
        products,
        totalSaved,
        savingsPerUnit,
        orderCount,
        savingsPerOrder,
        dateFrom,
        dateTo,
        datePreset,
        lastRequestParamsPresent,
    } = useReportsPageSavingsSummaryProperty();

    const handleBack = useCallback(() => {
        navigate(getRouteReportsSavingsSummary(), {
            state: { fromDrillIn: true },
        });
    }, [navigate]);

    const handleProductRowClick = useCallback(
        (sku: string, product: ProductMetricsDto) => {
            navigate(getRouteReportsSavingsSummaryProduct(sku), {
                state: {
                    product,
                    fromMissedSavings: false,
                },
            });
        },
        [navigate],
    );

    if (isSavingsSummaryVisible === undefined) {
        return null;
    }

    if (!isSavingsSummaryVisible) {
        return <Navigate to={getRouteForbidden()} replace />;
    }

    if (!propertyId || !lastRequestParamsPresent) {
        return <Navigate to={getRouteReportsSavingsSummary()} replace />;
    }

    return (
        <Page
            dataTestId="reports-page-savings-summary-property"
            padding="24px 35px"
        >
            <Box>
                <ReportDrillInHeader
                    title={propertyName}
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    datePreset={datePreset}
                    onBack={handleBack}
                    backButtonTitle={t('Savings Summary')}
                    backButtonId="back-to-savings-summary-button"
                />

                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 2,
                        marginBottom: 3,
                    }}
                >
                    <ReportMetric
                        title={t('savingsSummary.totalSavings')}
                        icon={<AttachMoneyIcon fontSize="small" />}
                        primary={formatReportCurrency(totalSaved)}
                        primaryColor="#16a34a"
                        loading={loading}
                        minHeight="100px"
                    />
                    <ReportMetric
                        title={t('savingsSummary.savingsPerUnit')}
                        icon={<AttachMoneyIcon fontSize="small" />}
                        primary={
                            savingsPerUnit != null
                                ? formatReportCurrency(savingsPerUnit)
                                : '—'
                        }
                        primaryColor="#16a34a"
                        loading={loading}
                        minHeight="100px"
                    />
                    <ReportMetric
                        title={t('savingsSummary.propertiesColumnOrders')}
                        icon={<ShoppingCartIcon fontSize="small" />}
                        primary={orderCount.toLocaleString()}
                        loading={loading}
                        minHeight="100px"
                    />
                    <ReportMetric
                        title={t(
                            'savingsSummary.propertyDrillInSavingsPerOrder',
                        )}
                        icon={<AttachMoneyIcon fontSize="small" />}
                        primary={
                            savingsPerOrder != null
                                ? formatReportCurrency(savingsPerOrder)
                                : '—'
                        }
                        primaryColor="#16a34a"
                        loading={loading}
                        minHeight="100px"
                    />
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '24px',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            gap: '24px',
                            marginBottom: '40px',
                        }}
                    >
                        <Box sx={{ flex: 1 }}>
                            <SavingsSummaryCategoriesSection
                                byCategory={byCategory}
                                loading={loading}
                                tableOnly
                                tableColumnIds={[
                                    'category',
                                    'savings',
                                    'totalSpend',
                                ]}
                                showBottomBorder={false}
                            />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <SavingsSummaryCategoriesSection
                                byCategory={byCategory}
                                loading={loading}
                                tableOnly
                                tableColumnIds={[
                                    'category',
                                    'savings',
                                    'totalSpend',
                                ]}
                                showBottomBorder={false}
                            />
                        </Box>
                    </Box>
                    <SavingsSummaryProductsSection
                        products={products}
                        totalSavingsAmount={summary?.savingsAmount}
                        loading={loading}
                        onOpenProductDrillIn={handleProductRowClick}
                        tableOnly
                        tableColumnIds={[
                            'item',
                            'brand',
                            'sku',
                            'properties',
                            'quantity',
                            'avgSupplierPrice',
                            'pricePaid',
                            'totalSaved',
                            'percentSaved',
                        ]}
                        subheader={t(
                            'savingsSummary.productsSubheaderAtProperty',
                            {
                                propertyNames: propertyName,
                            },
                        )}
                        showBottomBorder={false}
                    />
                </Box>
            </Box>
        </Page>
    );
};
